// app/components/common/Button/TextButton.tsx
'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import { Icon } from '../Icon';
import { theme } from '../../theme';
import { cn } from '@workspace/lib';

export const textButtonVariants = cva(
  'inline-flex items-center gap-3 rounded-[5px] font-semibold focus:outline-none disabled:opacity-50 disabled:pointer-events-none cursor-pointer transition-colors',
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-white hover:bg-primary-500 focus:ring-primary-500 disabled:bg-secondary-100 disabled:text-secondary-300',
        secondary:
          'bg-white text-primary border border-primary hover:bg-accent focus:ring-primary-500 disabled:text-secondary-200 disabled:border-secondary-300',
        tertiary:
          'bg-secondary-400 text-white hover:bg-secondary-300 focus:ring-primary-500 disabled:bg-secondary-100 disabled:text-secondary-300',
        text: 'bg-transparent text-primary hover:underline focus:ring-primary disabled:text-secondary-300',
      },
      site: {
        porta: '',
        portaNZ: '',
        redRec: '',
      },
    },
    compoundVariants: [
      {
        variant: 'secondary',
        site: 'redRec',
        class: 'hover:bg-primary-100',
      },
    ],
    defaultVariants: { variant: 'text', site: 'porta' },
  },
);

type TextButtonVariant = NonNullable<VariantProps<typeof textButtonVariants>['variant']>;

export interface TextButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'> {
  variant?: TextButtonVariant;
  isDisabled?: boolean;
  iconLeft?: React.ComponentProps<typeof Icon>['name'];
  iconRight?: React.ComponentProps<typeof Icon>['name'];
  iconSize?: number;
  className?: string;
}

export function TextButton({
  children,
  isDisabled = false,
  iconLeft,
  iconRight,
  iconSize = 20,
  className,
  variant,
  ...rest
}: TextButtonProps) {
  return (
    <button
      type="button"
      className={cn(textButtonVariants({ variant, site: theme }), className)}
      disabled={isDisabled}
      aria-disabled={isDisabled || undefined}
      {...rest}
    >
      {iconLeft && <Icon name={iconLeft} size={iconSize} />}
      <span className="flex-1 text-center">{children}</span>
      {iconRight && <Icon name={iconRight} size={iconSize} />}
    </button>
  );
}
